import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Loader2, FileText, Link as LinkIcon, Image as ImageIcon, Paperclip, ExternalLink, StickyNote } from "lucide-react";
import { toast } from "sonner";
import OwnershipDisclaimer from "@/components/OwnershipDisclaimer";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const bytesFmt = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Full read-only preview of a reported product — the admin can open any
 * attachment, read every note, and see the source thread in context before
 * dismissing or removing the listing. No purchase or ownership needed.
 */
export default function AdminProductPreviewDialog({ productId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !productId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    api.get(`/admin/products/${productId}/preview`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Couldn't load preview");
        onOpenChange(false);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, productId, onOpenChange]);

  const prod = data?.product;
  const items = data?.items || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-preview-dialog"
        className="bg-[#0A0A0A] border-[#27272A] text-[#F3F4F6] sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#3B82F6]" />
            {prod?.name || "Preview"}
          </DialogTitle>
          <DialogDescription className="text-[#9CA3AF] text-xs">
            Full read-only view · {items.length} item{items.length === 1 ? "" : "s"}
            {data?.seller?.name ? ` · by ${data.seller.name}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-[#6B7280] text-sm gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading contents…
            </div>
          ) : !data ? null : (
            <div className="space-y-4">
              {prod?.description && (
                <div className="rounded-xl bg-[#121212] border border-[#1E1E1E] p-3 text-sm text-[#D1D5DB] whitespace-pre-wrap">
                  {prod.description}
                  <div className="mt-2"><OwnershipDisclaimer product={prod} /></div>
                </div>
              )}

              {items.length === 0 ? (
                <div className="text-center py-10 text-sm text-[#6B7280]">
                  No items in this listing's source thread.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {items.map((it, idx) => (
                    <AdminPreviewItem key={it.item_id || idx} item={it} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdminPreviewItem({ item }) {
  const type = item.type || "note";
  const bg = item.color || "#1E1E1E";
  if (type === "link" && item.url) {
    return (
      <article
        data-testid={`admin-preview-item-${item.item_id}`}
        className="rounded-xl border border-[#1E1E1E] bg-[#121212] p-3 hover:border-[#3F3F46] transition-colors"
      >
        <div className="flex items-start gap-2.5">
          {item.preview?.image ? (
            <img src={item.preview.image} alt="" className="h-14 w-14 object-cover rounded-lg shrink-0 border border-[#27272A]" />
          ) : (
            <div className="h-14 w-14 rounded-lg shrink-0 bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center">
              <LinkIcon className="h-5 w-5 text-[#9CA3AF]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-0.5 flex items-center gap-1.5">
              LINK · {fmtDate(item.created_at)}
            </div>
            <div className="text-sm font-semibold text-[#F3F4F6] truncate">{item.preview?.title || item.url}</div>
            {item.preview?.description && (
              <div className="text-xs text-[#9CA3AF] mt-0.5 line-clamp-2">{item.preview.description}</div>
            )}
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-[#3B82F6] hover:text-[#60A5FA] mt-1.5"
            >
              {item.preview?.site || item.url} <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </article>
    );
  }

  if (type === "file" && item.file_url) {
    const isImage = /^image\//.test(item.file_type || "") || /\.(png|jpe?g|gif|webp|avif|heic)$/i.test(item.file_name || "");
    return (
      <article
        data-testid={`admin-preview-item-${item.item_id}`}
        className="rounded-xl border border-[#1E1E1E] bg-[#121212] p-3"
      >
        <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5 flex items-center gap-1.5">
          <Paperclip className="h-3 w-3" /> FILE · {fmtDate(item.created_at)}
        </div>
        {isImage ? (
          <a href={item.file_url} target="_blank" rel="noreferrer" className="block">
            <img
              src={item.file_url}
              alt={item.file_name || "attachment"}
              className="max-h-80 w-auto rounded-lg border border-[#27272A]"
            />
          </a>
        ) : (
          <a
            href={item.file_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-lg bg-[#1E1E1E] border border-[#27272A] px-3 py-2 hover:border-[#3B82F6] transition-colors"
          >
            <ImageIcon className="h-5 w-5 text-[#9CA3AF] shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[#F3F4F6] truncate">{item.file_name || "attachment"}</div>
              <div className="text-[10px] text-[#6B7280]">{item.file_type} · {bytesFmt(item.file_size)}</div>
            </div>
            <ExternalLink className="h-4 w-4 text-[#6B7280]" />
          </a>
        )}
        {item.content && (
          <div className="text-sm text-[#D1D5DB] whitespace-pre-wrap mt-2">{item.content}</div>
        )}
      </article>
    );
  }

  // note (text)
  return (
    <article
      data-testid={`admin-preview-item-${item.item_id}`}
      className="rounded-xl border border-[#1E1E1E] p-3"
      style={{ backgroundColor: `${bg}22`, borderColor: `${bg}66` }}
    >
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1 flex items-center gap-1.5">
        <StickyNote className="h-3 w-3" /> NOTE · {fmtDate(item.created_at)}
      </div>
      <div className="text-sm text-[#F3F4F6] whitespace-pre-wrap break-words leading-relaxed">
        {item.content || "(empty)"}
      </div>
    </article>
  );
}
